import {
  FulfillmentOrderLineItem,
  getLineItemGroupingKey,
  groupFulfillmentOrderLineItems,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { StorageLocationType } from "@redotech/redo-fulfillment-wms-common/schema/storage-location";
import { PICKABLE_BIN_TYPES } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import type { InventoryLevelId } from "@redotech/redo-warehouse-id";

const PICKABLE_BIN_TYPE_VALUES: ReadonlySet<string> = new Set(
  PICKABLE_BIN_TYPES,
);
const RECEIVING_BIN_TYPE: string = StorageLocationType.RECEIVING;

export function groupProblemPickLineItems({
  items,
  pickedQuantityByExternalId,
  degroupByCustomAttributes,
}: {
  items: FulfillmentOrderLineItem[];
  pickedQuantityByExternalId: Map<string, number>;
  degroupByCustomAttributes: boolean;
}): ProblemPickLineItemGroup[] {
  const groups = new Map<string, FulfillmentOrderLineItem[]>();
  const groupingConfig = { degroupByCustomAttributes };

  for (const item of items) {
    const key = JSON.stringify([
      getLineItemGroupingKey(item, groupingConfig),
      item.imsInventoryItemId ?? null,
    ]);
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  }

  return [...groups.values()].flatMap((originalItems) => {
    const item = groupFulfillmentOrderLineItems(
      originalItems,
      groupingConfig,
    )[0];
    if (!item) {
      return [];
    }
    return [
      {
        item,
        originalItems,
        pickedQuantity: originalItems.reduce(
          (total, originalItem) =>
            total +
            (pickedQuantityByExternalId.get(originalItem.externalId) ?? 0),
          0,
        ),
      },
    ];
  });
}

export function takeProblemPickSources({
  sources,
  quantity,
}: {
  sources: ProblemPickSource[];
  quantity: number;
}): ProblemPickSourceAllocation {
  const selectedSources: ProblemPickSource[] = [];
  const remainingSources: ProblemPickSource[] = [];
  let missingQuantity = quantity;

  for (const source of sources) {
    const quantityToTake = Math.min(source.quantity, missingQuantity);

    if (quantityToTake > 0) {
      selectedSources.push({ ...source, quantity: quantityToTake });
      missingQuantity -= quantityToTake;
    }

    const quantityRemaining = source.quantity - quantityToTake;
    if (quantityRemaining > 0) {
      remainingSources.push({ ...source, quantity: quantityRemaining });
    }
  }

  return { selectedSources, remainingSources, missingQuantity };
}

/** Spreads a picked quantity across a row's line items as absolute
 *  `setFulfillmentGroupPickedQuantities` updates, each with its share of sources. */
export function buildProblemPickQuantityUpdates({
  originalItems,
  pickedQuantityByExternalId,
  quantity,
  sources,
}: {
  originalItems: readonly Pick<
    FulfillmentOrderLineItem,
    "externalId" | "quantity"
  >[];
  pickedQuantityByExternalId: ReadonlyMap<string, number>;
  quantity: number;
  sources: ProblemPickSource[] | undefined;
}): ProblemPickQuantityUpdate[] {
  const updates: ProblemPickQuantityUpdate[] = [];
  let remainingToPick = quantity;
  let remainingSources = sources;

  for (const originalItem of originalItems) {
    if (remainingToPick <= 0) {
      break;
    }

    const currentPicked =
      pickedQuantityByExternalId.get(originalItem.externalId) ?? 0;
    const itemRemaining = originalItem.quantity - currentPicked;
    if (itemRemaining <= 0) {
      continue;
    }

    const quantityForItem = Math.min(remainingToPick, itemRemaining);
    let sourcesForItem: ProblemPickSource[] | undefined;
    if (remainingSources) {
      const allocation = takeProblemPickSources({
        sources: remainingSources,
        quantity: quantityForItem,
      });
      if (allocation.missingQuantity > 0) {
        throw new Error(
          "Selected inventory does not match the exact item being picked",
        );
      }
      remainingSources = allocation.remainingSources;
      sourcesForItem = allocation.selectedSources;
    }

    updates.push({
      fulfillmentOrderLineItemExternalId: originalItem.externalId,
      quantityPicked: currentPicked + quantityForItem,
      sources: sourcesForItem,
    });
    remainingToPick -= quantityForItem;
  }

  return updates;
}

export interface ProblemPickQuantityUpdate {
  fulfillmentOrderLineItemExternalId: string;
  quantityPicked: number;
  sources?: ProblemPickSource[];
}

/** First pickable bin that can cover the whole quantity on its own; callers
 *  order bins by preference (receiving last). */
export function findCoveringPickableBin<
  Bin extends { binType?: string | null; availableQuantity: number },
>(bins: readonly Bin[], quantity: number): Bin | undefined {
  return bins.find(
    (bin) =>
      (bin.binType == null || PICKABLE_BIN_TYPE_VALUES.has(bin.binType)) &&
      bin.availableQuantity >= quantity,
  );
}

export interface PickableBinSummary<StorageLocationId extends string = string> {
  storageLocationId: StorageLocationId;
  binName: string;
  binType: string | null;
  availableQuantity: number;
}

/** One entry per bin a problem pick can pull from (counted, stocked, pickable type,
 *  required exact item), regular bins before receiving, then most stock first. */
export function summarizePickableBins<StorageLocationId extends string>(
  rows: readonly {
    storageLocationId: StorageLocationId;
    binName: string;
    binType?: string | null;
    availableQuantity: number;
    lastCountedAt?: Date | null;
    inventoryItemId?: string | null;
  }[],
  requiredInventoryItemId: string | null,
): PickableBinSummary<StorageLocationId>[] {
  const byBin = new Map<
    StorageLocationId,
    PickableBinSummary<StorageLocationId>
  >();
  for (const row of rows) {
    const binType = row.binType ?? null;
    if (
      row.lastCountedAt == null ||
      row.availableQuantity <= 0 ||
      (binType !== null && !PICKABLE_BIN_TYPE_VALUES.has(binType)) ||
      (requiredInventoryItemId !== null &&
        row.inventoryItemId !== requiredInventoryItemId)
    ) {
      continue;
    }
    const summary = byBin.get(row.storageLocationId) ?? {
      storageLocationId: row.storageLocationId,
      binName: row.binName,
      binType,
      availableQuantity: 0,
    };
    summary.availableQuantity += row.availableQuantity;
    byBin.set(row.storageLocationId, summary);
  }
  return [...byBin.values()].sort((a, b) => {
    const aReceiving = a.binType === RECEIVING_BIN_TYPE;
    const bReceiving = b.binType === RECEIVING_BIN_TYPE;
    if (aReceiving !== bReceiving) {
      return aReceiving ? 1 : -1;
    }
    if (a.availableQuantity !== b.availableQuantity) {
      return b.availableQuantity - a.availableQuantity;
    }
    return a.binName.localeCompare(b.binName);
  });
}

/** Labels item-specific stock; pooled (non-item) stock needs no label. */
export function getProblemPickInventoryIdentity(
  inventoryItemId: string | null | undefined,
): string | null {
  return inventoryItemId ? `Item: ${inventoryItemId.slice(-8)}` : null;
}

export type ProblemPickSource =
  | {
      type: "inventoryLevel";
      inventoryLevelId: InventoryLevelId;
      quantity: number;
    }
  | { type: "external"; quantity: number };

interface ProblemPickSourceAllocation {
  selectedSources: ProblemPickSource[];
  remainingSources: ProblemPickSource[];
  missingQuantity: number;
}

interface ProblemPickLineItemGroup {
  item: FulfillmentOrderLineItem;
  originalItems: FulfillmentOrderLineItem[];
  pickedQuantity: number;
}
